/**
 * Floating demo persona switcher. Only renders in dev mode.
 *
 * Lets you swap between pre-seeded demo users without using multiple
 * browser windows / incognito sessions. Click a persona → logs in as
 * them via the auth API → updates the auth store → navigates to their
 * dashboard.
 *
 * Strip from production by guarding on `import.meta.env.DEV`.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";

import { login } from "@/features/auth/api/authApi";
import { useAuthStore } from "@/features/auth/model/authStore";
import { useOpsStore } from "@/features/ops/model/opsStore";
import { useShiftFeedStore } from "@/features/shifts/model/shiftFeedStore";
import { cn } from "@/shared/lib/cn";

interface Persona {
  label: string;
  email: string;
  emoji: string;
  destination: string;
  description: string;
}

const PERSONAS: Persona[] = [
  {
    label: "Owen (Venue)",
    email: "owen@demo.oneshift.ca",
    emoji: "🏪",
    destination: "/venue",
    description: "Test Diner — posts shifts, confirms applicants",
  },
  {
    label: "Sam (Worker)",
    email: "sam@demo.oneshift.ca",
    emoji: "👨‍🍳",
    destination: "/worker",
    description: "Approved worker — apply, check in, rate",
  },
];

const OPS_TOKEN = "change-me-to-a-random-ops-token";

export function DemoSwitcher() {
  if (!import.meta.env.DEV) return null;

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const setSession = useAuthStore((s) => s.setSession);
  const currentUser = useAuthStore((s) => s.user);
  const setOpsToken = useOpsStore((s) => s.setToken);
  const resetShiftFeed = useShiftFeedStore((s) => s.reset);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const switchTo = async (persona: Persona) => {
    setBusy(persona.email);
    try {
      const res = await login({ email: persona.email, password: "demo123" });
      setSession(res.access_token, res.user);
      qc.clear();
      // Persisted UI prefs (shift-feed availability filter) would otherwise
      // leak from the previous persona's session into the new one.
      resetShiftFeed();
      navigate(persona.destination);
      setOpen(false);
    } catch (err) {
      console.error("Demo switch failed:", err);
      alert(`Could not switch to ${persona.label}. Did you re-seed the DB?`);
    } finally {
      setBusy(null);
    }
  };

  const enterOps = () => {
    setOpsToken(OPS_TOKEN);
    navigate("/ops");
    setOpen(false);
  };

  const currentLabel =
    PERSONAS.find((p) => p.email === currentUser?.email)?.label ??
    currentUser?.email ??
    "Not signed in";

  return (
    <div className="fixed bottom-20 right-4 z-[100] md:bottom-4">
      {open ? (
        <div className="w-80 animate-fade-in-up rounded-xl border border-border bg-surface-raised p-4 shadow-elevated">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider text-copy-subtle">
              Demo persona
            </p>
            <button
              onClick={() => setOpen(false)}
              className="rounded-md p-1 text-copy-subtle transition-colors hover:bg-surface-sunken hover:text-copy"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="mb-3 text-xs text-copy-muted">
            Current: <span className="font-medium text-copy">{currentLabel}</span>
          </p>
          <div className="space-y-1.5">
            {PERSONAS.map((p) => {
              const active = currentUser?.email === p.email;
              return (
                <button
                  key={p.email}
                  onClick={() => switchTo(p)}
                  disabled={busy !== null}
                  className={cn(
                    "flex w-full items-start gap-2 rounded-md p-2 text-left text-sm transition-colors",
                    active
                      ? "bg-primary text-primary-foreground"
                      : "text-copy hover:bg-surface-sunken disabled:opacity-50",
                  )}
                >
                  <span className="text-lg leading-none">{p.emoji}</span>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{p.label}</div>
                    <div
                      className={cn(
                        "truncate text-xs",
                        active ? "text-primary-foreground/80" : "text-copy-muted",
                      )}
                    >
                      {p.description}
                    </div>
                  </div>
                  {busy === p.email && <span className="text-xs">…</span>}
                </button>
              );
            })}
            <button
              onClick={enterOps}
              className="flex w-full items-center gap-2 rounded-md p-2 text-left text-sm text-copy transition-colors hover:bg-surface-sunken"
            >
              <span className="text-lg leading-none">🛠️</span>
              <div className="flex-1">
                <div className="font-medium">Ops panel</div>
                <div className="truncate text-xs text-copy-muted">Token-gated admin view</div>
              </div>
            </button>
          </div>
          <p className="mt-3 border-t border-border pt-2 text-[10px] text-copy-subtle">
            Dev only — strips out of production builds.
          </p>
        </div>
      ) : (
        <button
          onClick={() => setOpen(true)}
          className="rounded-full bg-primary px-3.5 py-2 text-xs font-medium text-primary-foreground shadow-card transition-all hover:-translate-y-0.5 hover:bg-primary-hover hover:shadow-card-hover"
        >
          🎭 Switch persona
        </button>
      )}
    </div>
  );
}
